import { Component, computed, ElementRef, Signal, signal, ViewChild } from '@angular/core';
import { NgClass } from '@angular/common';
import { map, Observable, of } from 'rxjs';
import { FieldPathDirective, FormNonFieldErrorsComponent, FormOptionsDirective } from '@bo/ng-forms';
import { Method } from '@bo/ng-api';
import { FieldComponent } from '../../../../../shared/components/forms/form-field/form-field.component';
import { EmployeeRequest, EmployeeRoleType, IdNumType } from '../../../../../_interfaces/employee.interface';
import { SubmitComponent } from '../../../../../shared/components/forms/submit/submit.component';
import { PhoneFieldComponent } from '../../../../../shared/components/forms/phone-field/phone-field.component';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { FieldUnverifiedTextComponent } from '../../../../../shared/components/forms/field-unverified-text/field-unverified-text.component';
import { IDFieldComponent } from '../../../../../shared/components/forms/id-field/id-field.component';
import { AvatarComponent } from '../../../../../shared/components/avatar/avatar.component';
import { LoaderComponent } from '../../../../../shared/components/loader/loader.component';
import { SelectFieldComponent } from '../../../../../shared/components/forms/select-field/select-field.component';
import { OffcanvasCancelBtnComponent } from '../../../../../shared/components/offcanvas/offcanvas-cancel-btn/offcanvas-cancel-btn.component';
import { EmployeeFormBaseComponent } from '../../add-employee/employee-form-base/employee-form-base.component';
import { ReactiveFormsModule } from '@angular/forms';
import { SelectTopupFrequencyComponent } from '../../../../../shared/components/forms/select-topup-frequency/select-topup-frequency.component';
import { CardLimitDisplayComponent } from '../../../../../shared/components/cards/card-limit-display/card-limit-display.component';
import { ToggleSwitchComponent } from '../../../../../shared/components/forms/toggle-switch/toggle-switch.component';
import { SetUserStateFromProfileLoad } from '../../../../../store/app/app.actions';

@Component({
  selector: 'app-employee-form',
  imports: [
    AvatarComponent,
    FieldComponent,
    FieldPathDirective,
    FieldUnverifiedTextComponent,
    FormNonFieldErrorsComponent,
    IDFieldComponent,
    LoaderComponent,
    NgbTooltip,
    OffcanvasCancelBtnComponent,
    PhoneFieldComponent,
    ReactiveFormsModule,
    SelectFieldComponent,
    SubmitComponent,
    NgClass,
    FormOptionsDirective,
    SelectTopupFrequencyComponent,
    CardLimitDisplayComponent,
    ToggleSwitchComponent,
  ],
  templateUrl: '../../add-employee/employee-form-base/employee-form-base.component.html',
})
export class EmployeeFormComponent extends EmployeeFormBaseComponent {
  @ViewChild('approversField', { read: ElementRef }) approversField!: ElementRef;
  initialEmail: string = '';
  initialPhone: string = '';
  initialID: string = '';
  initialIDType: IdNumType | undefined;
  protected loggedInUserEmail = signal<string>('');

  protected setIssueCardFromParams: Signal<boolean> = computed(() => {
    return this.route.snapshot.queryParams['issue_card'] === 'true';
  });

  override ngOnInit() {
    super.ngOnInit();
    // set and fetch latest user details to ensure system knows the logged in user after contact details has changed
    // "off site" and page has not been reloaded
    this.store.dispatch(new SetUserStateFromProfileLoad());
    this.subscriptions.add(
      this.userProfile$.subscribe((profile) => {
        if (profile) {
          this.loggedInUserEmail.set(profile.email);
        }
      }),
    );
  }

  override createForm(): void {
    const options$ = this.employeeApiService.getOptions(this.employeeApiService.URL_EMPLOYEE_SIGNUP + '/', Method.POST);
    const data$ = this.getInitialData();

    // options$

    this.subscriptions.add(
      this.buildFromOptions(options$, data$).subscribe({
        next: () => {
          this.handleInitialDataSubscription(data$);
          this.handleRoleValueChanges();
          this.handlePhoneValueChanges();
          this.handleEmailValueChanges();
          this.handleCreateCardToggleChanges();
          this.handleTopUpAmountChanges();

          // Don't show or edit this field after the employee is created.
          // It's only used for initial sign-up validation and KYC
          if (this.employeeId()) {
            this.form.get('date_of_birth')?.disable();
          }

          // prevent new employee invitations if no cards available to issue
          if (this.showNoCardsAvailable()) {
            this.form.get('role')?.setValue(EmployeeRoleType.ADMIN);
            this.form.get('create_card')?.setValue(false);
          }

          this.subscriptions.add(
            this.form.statusChanges.subscribe(() => {
              this.isFormInvalid.set(this.form.invalid);
            }),
          );
          // Initial set
          this.isFormInvalid.set(this.form.invalid);
        },
      }),
    );
  }

  protected getInitialData() {
    if (!this.employeeId()) {
      return of({} as EmployeeRequest);
    }
    return this.employeeApiService.getEmployeeById(this.employeeId()).pipe(
      map((employee) => {
        this.initialEmail = employee.email;
        this.initialPhone = employee.phone;
        this.initialID = employee.idnum;
        this.initialIDType = employee.idnum_type;
        const approverIds: string[] = [];
        if (employee.approvers) {
          for (const approver of employee.approvers) {
            approverIds.push(approver.id);
          }
        }
        // Convert data to the expected format
        const data: EmployeeRequest = {
          first_name: employee.first_name,
          last_name: employee.last_name,
          email: employee.email,
          phone: employee.phone,
          idnum: employee.idnum,
          idnum_type: employee.idnum_type,
          role: employee.role,
          create_card: employee.is_card_holder,
          approvers: approverIds,
        };
        this.employeeData.set(employee);
        return data;
      }),
    );
  }

  protected handleInitialDataSubscription(data$: Observable<EmployeeRequest>): void {
    this.subscriptions.add(
      data$.subscribe((data) => {
        if (!this.employeeData()?.id) return;
        this.patchFormData();
        this.disableFieldsBasedOnConditions(data);
      }),
    );
  }

  protected patchFormData(): void {
    this.form.get('create_card')?.setValue(this.employeeData()?.is_card_holder);

    if (this.setIssueCardFromParams()) {
      this.fadeContainer.nativeElement.classList.add('highlight');
      this.form.get('create_card')?.setValue(true);
      setTimeout(() => {
        this.scrollToBottom();
        // Focus the approvers field
        const ngSelectElement = this.approversField?.nativeElement?.querySelector('.ng-select');
        if (ngSelectElement) {
          const inputElement = ngSelectElement.querySelector('input');
          inputElement?.focus();
        }
      }, 100);
      setTimeout(() => {
        this.fadeContainer.nativeElement.classList.remove('highlight');
      }, 2000);
    }
  }

  protected disableFieldsBasedOnConditions(data: EmployeeRequest): void {
    if (this.employeeData()) {
      //dont allow existing employees to edit topup data
      if (this.employeeId()) {
        this.showRecurringSwitch.set(false);
        this.form.get('topup_amount')?.disable();
      }
      if (this.employeeData()?.is_card_holder || this.employeeData()?.role === EmployeeRoleType.USER) {
        this.form.get('create_card')?.disable();
      }
      if (this.employeeData()?.idnum_status !== null) {
        this.form.get('idnum_type')?.disable();
        this.form.get('idnum')?.disable();
      }
      if (!this.employeeData()?.phone_verified) this.form.get('email')?.disable();
      if (!this.employeeData()?.email_verified) this.form.get('phone')?.disable();
      if (this.loggedInUserEmail() === data.email) this.form.get('role')?.disable();
      this.setCardFields();
    }
  }

  // handle if user is admin
  protected override toggleCreateCard(): void {
    const hadSelectedCard = this.form.get('create_card')?.value;
    if (this.employeeData()?.is_card_holder) {
      this.form.get('create_card')?.disable();
    } else {
      this.form.get('create_card')?.enable();
    }
    this.form.get('create_card')?.setValue(this.employeeData()?.is_card_holder ?? hadSelectedCard);
    this.showApproverFields.set(this.employeeData()?.is_card_holder ?? hadSelectedCard);
    this.setCardFields();
  }

  protected setCardFields() {
    // if editing existing employee
    if (this.employeeId() && this.employeeData()?.is_card_holder) {
      // if employee has a card already
      // never allow editing of recurring topup
      this.form.get('topup_amount')?.disable();
      this.showRecurringTopUp.set(false);
    } else {
      // if NEW employee or
      // Employee did not have a card, and gets one now (switching employee type admin to User), allow recurring topup
      this.showRecurringSwitch.set(true);
    }
  }

  protected handlePhoneValueChanges(): void {
    this.subscriptions.add(
      this.form.controls['phone'].valueChanges.subscribe((value) => {
        if (this.initialPhone && value) {
          // The phone field returns E.164 format (e.g., "+27721231234")
          this.isEditingPhone = value !== this.initialPhone;
        }
        this.toggleEmailBasedOnPhone();
      }),
    );
  }

  protected toggleEmailBasedOnPhone(): void {
    const emailControl = this.form.get('email');
    if (this.isEditingPhone && !emailControl?.disabled) {
      emailControl?.disable({ emitEvent: false });
    } else if (!this.isEditingPhone && emailControl?.disabled) {
      emailControl?.enable({ emitEvent: false });
    }
  }

  protected handleEmailValueChanges(): void {
    this.subscriptions.add(
      this.form.controls['email'].valueChanges.subscribe((value) => {
        if (this.initialEmail) {
          this.isEditingEmail = value !== this.initialEmail;
        }
        this.togglePhoneBasedOnEmail();
      }),
    );
  }

  protected togglePhoneBasedOnEmail(): void {
    const phoneControl = this.form.get('phone');
    if (this.isEditingEmail && !phoneControl?.disabled) {
      phoneControl?.disable({ emitEvent: false });
    } else if (!this.isEditingEmail && phoneControl?.disabled) {
      phoneControl?.enable({ emitEvent: false });
    }
  }

  private updateExistingEmployee(formData: EmployeeRequest): Observable<EmployeeRequest> {
    const data: Partial<EmployeeRequest> = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      role: formData.role,
      create_card: formData.create_card,
    };

    if (formData.approvers?.length) data.approvers = formData.approvers ?? [];

    // if existing admin user gets updated to User role, create card with top up rules.
    if (formData.create_card && this.showRecurringTopUp()) {
      data.topup_frequency = formData.topup_frequency;
      data.topup_amount = formData.topup_amount;
    }

    // BE expects either phone or email value, not both
    if (!this.employeeData()?.phone_verified || this.isEditingPhone) {
      data.phone = formData.phone;
    }
    if (!this.employeeData()?.email_verified || this.isEditingEmail) {
      data.email = formData.email;
    }

    if (formData.idnum !== this.initialID) data.idnum = formData.idnum;
    if (formData.idnum_type !== this.initialIDType) data.idnum_type = formData.idnum_type;

    return this.employeeApiService.updateEmployee(this.employeeId(), data as EmployeeRequest);
  }

  override submitData(formData: EmployeeRequest): Observable<EmployeeRequest> {
    if (this.showRecurringTopUp()) {
      formData.topup_frequency = this.selectedFrequency();
    }

    if (this.employeeId()) {
      // Existing Admin user got role changed from Admin to User && he has not had an issued card before
      if (
        this.employeeData()?.role === EmployeeRoleType.ADMIN &&
        formData.role === EmployeeRoleType.USER &&
        !this.employeeData()?.is_card_holder
      ) {
        formData.create_card = this.form.get('create_card')?.value;
      }
      this.payload = formData; // used for modal data display
      return this.updateExistingEmployee(formData);
    } else {
      // New user
      if (formData.role === EmployeeRoleType.ADMIN) {
        formData.create_card = Boolean(formData.create_card);
      }
      return this.employeeApiService.inviteEmployee(formData);
    }
  }
}
